/**
 * CreditCheckActionModal — Extracted from ImmeublePortal.jsx (Batch #12 refactor).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Banknote, ShieldCheck, Loader2, FileDown } from "lucide-react";
import { BaseActionModal, FieldLabel, API, authH } from "./shared";

export function CreditCheckActionModal({
  apartments, buildings, selectedBuilding, selectedApt,
  onBuildingChange, onAptChange, onClose, onReload,
}) {
  const [bId, setBId] = useState(selectedBuilding?.id || "");
  const [uId, setUId] = useState(selectedApt?.id || "");
  const aptsOfB = apartments.filter((a) => a.property_id === bId);
  const [dl, setDl] = useState(null);

  // Rapport de locataire (PDF du cabinet) déposé au coffre-fort → téléchargement
  const downloadReport = async (c) => {
    setDl(c.id);
    try {
      const r = await axios.get(`${API}/portal/immeuble/safe/${c.report_safe_doc_id}/download`, { headers: authH(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rapport-locataire-${(c.candidate_last_name || "candidat").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Rapport indisponible pour le moment — contactez le cabinet" : "Erreur lors du téléchargement du rapport");
    } finally {
      setDl(null);
    }
  };

  const [form, setForm] = useState({
    candidate_first_name: "",
    candidate_last_name: "",
    candidate_email: "",
    candidate_phone: "",
    candidate_dob: "",
    // Batch #7 — adresse structurée à 5 champs
    candidate_address_civic_number: "",
    candidate_address_street: "",
    candidate_address_city: "",
    candidate_address_postal_code: "",
    candidate_address_label: "",
    notes: "",
    consent_obtained: false,
  });
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/immeuble/credit-checks`, { headers: authH() });
      setItems(r.data?.items || []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  };
  const fetchPricing = async () => {
    try {
      const r = await axios.get(`${API}/portal/immeuble/credit-check-pricing`, { headers: authH() });
      setPricing(r.data || null);
    } catch (_e) {
      setPricing({ amount_total: 35, amount_subtotal: 35, include_taxes: false });
    }
  };
  useEffect(() => { fetchList(); fetchPricing(); }, []);

  const submit = async () => {
    if (!form.candidate_first_name || !form.candidate_last_name) { toast.error("Prénom et nom requis"); return; }
    if (!form.candidate_dob) { toast.error("Date de naissance requise"); return; }
    if (!form.candidate_email) { toast.error("Courriel requis"); return; }
    if (!form.candidate_phone) { toast.error("Téléphone requis"); return; }
    if (!form.candidate_address_civic_number || !form.candidate_address_street ||
        !form.candidate_address_city || !form.candidate_address_postal_code) {
      toast.error("Adresse actuelle incomplète"); return;
    }
    if (!form.consent_obtained) { toast.error("Le consentement écrit du candidat est obligatoire"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/portal/immeuble/credit-checks`, {
        ...form,
        property_id: bId || null,
        unit_id: uId || null,
      }, { headers: authH() });
      toast.success("Demande d'enquête envoyée à l'équipe");
      setForm({
        candidate_first_name: "", candidate_last_name: "", candidate_email: "",
        candidate_phone: "", candidate_dob: "",
        candidate_address_civic_number: "", candidate_address_street: "",
        candidate_address_city: "", candidate_address_postal_code: "",
        candidate_address_label: "",
        notes: "", consent_obtained: false,
      });
      fetchList();
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BaseActionModal
      title="Enquête de crédit"
      subtitle="Vérification d'un candidat locataire — rapport de locataire du cabinet"
      tone="cyan"
      icon={ShieldCheck}
      testId="immeuble-action-modal-credit"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Fermer</button>
          <button
            onClick={submit}
            disabled={busy || !form.consent_obtained}
            className="px-4 py-1.5 text-sm rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="immeuble-credit-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            Soumettre l'enquête
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {/* Card pricing dynamique (Batch #7 — visuel modernisé Batch #14.35) */}
        {pricing && (
          <div className="rounded-xl bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 p-4 shadow-sm" data-testid="immeuble-credit-pricing-card">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-cyan-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-700">Frais du service</div>
                    <div className="text-sm font-semibold text-cyan-950 leading-tight">Rapport de locataire — crédit (Equifax), archives publiques et identité</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold font-mono text-cyan-900" data-testid="immeuble-credit-pricing-total">
                      {Number(pricing.amount_total).toFixed(2)} $
                    </div>
                    <div className="inline-flex items-center gap-1 text-[10px] font-semibold text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded-full mt-0.5">
                      ✓ Faible coût · Tarif unique
                    </div>
                  </div>
                </div>
                {pricing.include_taxes && (
                  <div className="mt-2 pt-2 border-t border-cyan-200 grid grid-cols-3 gap-2 text-[10px] text-cyan-800">
                    <div>Sous-total <b className="font-mono block">{Number(pricing.amount_subtotal).toFixed(2)} $</b></div>
                    <div>TPS ({pricing.tps_rate}%) <b className="font-mono block">{Number(pricing.tps_amount).toFixed(2)} $</b></div>
                    <div>TVQ ({pricing.tvq_rate}%) <b className="font-mono block">{Number(pricing.tvq_amount).toFixed(2)} $</b></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <FieldLabel label="Immeuble">
            <select value={bId} onChange={(e) => { setBId(e.target.value); setUId(""); onBuildingChange?.(e.target.value); }} className="cc-input">
              <option value="">— Aucun spécifique —</option>
              {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Appartement visé">
            <select value={uId} onChange={(e) => { setUId(e.target.value); onAptChange?.(e.target.value); }} className="cc-input" data-testid="immeuble-credit-unit">
              <option value="">— Aucun —</option>
              {aptsOfB.map((a) => <option key={a.id} value={a.id}>Apt {a.unit_label || a.apt_number || "?"}</option>)}
            </select>
          </FieldLabel>
        </div>

        {/* Identité — Batch #7 */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
          <h4 className="text-[10px] uppercase font-bold text-slate-700 tracking-wider">Identité du candidat</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <FieldLabel label="Prénom" required>
              <input value={form.candidate_first_name} onChange={(e) => upd("candidate_first_name", e.target.value)} maxLength={60} className="cc-input" data-testid="immeuble-credit-firstname" />
            </FieldLabel>
            <FieldLabel label="Nom" required>
              <input value={form.candidate_last_name} onChange={(e) => upd("candidate_last_name", e.target.value)} maxLength={60} className="cc-input" data-testid="immeuble-credit-lastname" />
            </FieldLabel>
            <FieldLabel label="Date de naissance" required>
              <input type="date" value={form.candidate_dob} onChange={(e) => upd("candidate_dob", e.target.value)} className="cc-input" data-testid="immeuble-credit-dob" />
            </FieldLabel>
          </div>
        </div>

        {/* Coordonnées — Batch #7 */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-2.5 space-y-2">
          <h4 className="text-[10px] uppercase font-bold text-slate-700 tracking-wider">Coordonnées</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <FieldLabel label="Numéro de téléphone" required>
              <input value={form.candidate_phone} onChange={(e) => upd("candidate_phone", e.target.value)} maxLength={30} className="cc-input font-mono" data-testid="immeuble-credit-phone" />
            </FieldLabel>
            <FieldLabel label="Courriel" required>
              <input type="email" value={form.candidate_email} onChange={(e) => upd("candidate_email", e.target.value)} maxLength={120} className="cc-input" data-testid="immeuble-credit-email" />
            </FieldLabel>
          </div>
        </div>

        {/* Adresse actuelle structurée (Batch #7 — 5 champs) */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-2.5 space-y-2" data-testid="immeuble-credit-address-block">
          <h4 className="text-[10px] uppercase font-bold text-slate-700 tracking-wider">Adresse actuelle</h4>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
            <div className="sm:col-span-2">
              <FieldLabel label="N° civique" required>
                <input value={form.candidate_address_civic_number} onChange={(e) => upd("candidate_address_civic_number", e.target.value)} maxLength={20} className="cc-input" data-testid="immeuble-credit-addr-civic" />
              </FieldLabel>
            </div>
            <div className="sm:col-span-6">
              <FieldLabel label="Rue" required>
                <input value={form.candidate_address_street} onChange={(e) => upd("candidate_address_street", e.target.value)} maxLength={120} className="cc-input" data-testid="immeuble-credit-addr-street" />
              </FieldLabel>
            </div>
            <div className="sm:col-span-4">
              <FieldLabel label="Libellé (app., bureau, etc.)">
                <input value={form.candidate_address_label} onChange={(e) => upd("candidate_address_label", e.target.value)} maxLength={60} className="cc-input" placeholder="ex: Apt 12B" data-testid="immeuble-credit-addr-label" />
              </FieldLabel>
            </div>
            <div className="sm:col-span-7">
              <FieldLabel label="Ville" required>
                <input value={form.candidate_address_city} onChange={(e) => upd("candidate_address_city", e.target.value)} maxLength={80} className="cc-input" data-testid="immeuble-credit-addr-city" />
              </FieldLabel>
            </div>
            <div className="sm:col-span-5">
              <FieldLabel label="Code postal" required>
                <input value={form.candidate_address_postal_code} onChange={(e) => upd("candidate_address_postal_code", e.target.value.toUpperCase())} maxLength={10} className="cc-input font-mono" placeholder="H1A 1A1" data-testid="immeuble-credit-addr-postal" />
              </FieldLabel>
            </div>
          </div>
        </div>

        <FieldLabel label="Notes (optionnel)">
          <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} maxLength={1000} className="cc-input" />
        </FieldLabel>

        <label className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer">
          <input type="checkbox" checked={form.consent_obtained} onChange={(e) => upd("consent_obtained", e.target.checked)} className="mt-0.5" data-testid="immeuble-credit-consent" />
          <span className="text-[11px] text-amber-900 leading-snug">
            <b>Je confirme</b> avoir obtenu le consentement écrit du candidat pour effectuer cette enquête de crédit, conformément à la Loi sur la protection des renseignements personnels (Québec).
          </span>
        </label>

        {items.length > 0 && (
          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Demandes récentes <span className="font-normal text-slate-400">({items.length})</span>
            </div>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />
            ) : (
              <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                {items.slice(0, 5).map((c) => (
                  <div key={c.id} className="text-[11px] flex items-center justify-between gap-2 p-1.5 rounded bg-slate-50" data-testid={`immeuble-credit-row-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800">{c.candidate_first_name} {c.candidate_last_name}{c.result_score ? <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-800 font-bold" data-testid={`immeuble-credit-score-${c.id}`}>Cote {c.result_score}</span> : null}</div>
                      <div className="text-[10px] text-slate-500 truncate">{c.created_at?.slice(0, 10)} · {c.candidate_email || c.candidate_phone || "—"}</div>
                    </div>
                    {c.report_safe_doc_id && (
                      <button
                        onClick={() => downloadReport(c)}
                        disabled={dl === c.id}
                        className="px-2 py-0.5 rounded bg-cyan-700 text-white text-[10px] font-semibold hover:bg-cyan-800 inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid={`immeuble-credit-report-${c.id}`}
                      >
                        {dl === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} Rapport PDF
                      </button>
                    )}
                    {c.payment_status !== "paid" && (
                      <button
                        onClick={async () => {
                          try {
                            const r = await axios.post(
                              `${API}/portal/immeuble/credit-checks/${c.id}/create-checkout`,
                              { origin_url: window.location.origin },
                              { headers: authH() }
                            );
                            if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Erreur Stripe");
                          }
                        }}
                        className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 inline-flex items-center gap-1"
                        data-testid={`immeuble-credit-pay-${c.id}`}
                      >
                        <Banknote className="w-3 h-3" /> Payer {pricing ? Number(pricing.amount_total).toFixed(2) : "35.00"} $
                      </button>
                    )}
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                      ["completed","approved"].includes(c.status) ? "bg-emerald-100 text-emerald-700"
                      : c.status === "in_progress" || c.status === "in_analysis" ? "bg-blue-100 text-blue-700"
                      : ["rejected","refused"].includes(c.status) ? "bg-rose-100 text-rose-700"
                      : "bg-amber-100 text-amber-700"
                    }`}>
                      {{
                        pending: "En attente",
                        in_progress: "En cours",
                        in_analysis: "En analyse",
                        completed: "Terminé",
                        approved: "Accepté",
                        rejected: "Refusé",
                        refused: "Refusé",
                      }[c.status] || c.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </BaseActionModal>
  );
}
